// material-ui
import { useColorScheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// project imports
// ZERCODE CHANGE [STD-FE]: Berry rendered its own static "Berry" svg here.
// White-label branding comes from the Control Plane tenant-branding response,
// so the logo is tenant-driven with a neutral text fallback.
import { useBranding, brandDisplayName, BRAND_LOGO_HEIGHT, BRAND_LOGO_MAX_WIDTH } from '@zercode/theme';

/**
 * ⚠ THE HEADER IS AS TALL AS THIS IMAGE, so the size is a CONSTRAINT, not a hint.
 *
 * This used to be a bare `height` ATTRIBUTE on the img — a presentational hint, the lowest-priority origin
 * in the cascade, which any author CSS beats and which says nothing about width at all. The tenant logo
 * arrives as an SVG sized in MILLIMETRES (294mm x 145mm, i.e. an intrinsic 1112x547 CSS px), so losing that
 * one attribute renders it ~112px tall in a 228px slot. Berry reserves a fixed offset for the header, so the
 * overflow landed on top of the breadcrumbs and the first button on every page — the shipped bug this pair
 * of constants exists to prevent.
 *
 * An `sx` height is a CLASS selector in the author origin, so it outranks both the presentational hint it
 * replaces and any element-selector rule. The constraint cannot be lost the same way twice.
 *
 * The shell now measures the AppBar rather than assuming 88px (see MainLayout/shellOffsets.ts), so a large
 * logo can no longer HIDE anything. Bounding it here is the other half: a 130px logo that merely pushes the
 * whole app down is still wrong, and no tenant should be able to do it by uploading a file.
 *
 * ⚠ BOTH BOUNDS NOW LIVE IN `@zercode/theme`, and the height is no longer this app's own number.
 * `BRAND_LOGO_HEIGHT` is the PUBLIC SITE's expression — a flat 32px here meant the same tenant's mark
 * rendered 55px on the public page and 32px one click later inside the app. `BRAND_LOGO_MAX_WIDTH` stays
 * app-shaped (the header's 228px box and the 260px drawer, less padding and the menu toggle beside it), and
 * is deliberately NOT the public site's wider ceiling. Three SPAs used to carry identical copies of these
 * two numbers; one home is what stops them drifting apart again. See that file for the full reasoning.
 *
 * `objectFit: contain` is what keeps a logo that hits the width bound in proportion instead of squashing it.
 */

// ==============================|| LOGO ||============================== //

export default function Logo({ dark = false }: { dark?: boolean }) {
  const branding = useBranding();
  const { colorScheme } = useColorScheme();

  const isDark = dark || colorScheme === 'dark';
  const src = isDark ? (branding.logo.dark ?? branding.logo.light) : (branding.logo.light ?? branding.logo.dark);

  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={branding.logo.altText ?? brandDisplayName(branding)}
        sx={{
          display: 'block',
          height: BRAND_LOGO_HEIGHT,
          width: 'auto',
          maxWidth: BRAND_LOGO_MAX_WIDTH,
          objectFit: 'contain'
        }}
      />
    );
  }

  return (
    <Typography variant="h3" component="span" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 0.5 }}>
      {brandDisplayName(branding)}
    </Typography>
  );
}
